import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { NodeSelection } from "components/shared/nodes/NodeSelection";
import { Observable } from "components/shared/nodes/Observable";
import {
  INode,
  INodeSelectionTracker,
  INodeTree,
} from "models/node";
import { IPolicyTemplateSlot, IPolicySlotConstraint } from "models/policySlot";
import { IPublishedTemplate } from "models/templates";
import { useUpdatePolicyMutation } from "services/policy";
import { environmentConfig } from "config/environmentConfig";
import { IPolicyInfo } from "../types";

// Simplified interface for policy editing
interface IPolicyNodeTreeProvider {
  nodeTree: INodeTree<IPolicyTemplateSlot>;
  nodeSelection: INodeSelectionTracker;
  getSelectedNodes: () => INode<IPolicyTemplateSlot>[];
  getChildNodes: (nodeId: string) => INode<IPolicyTemplateSlot>[];
  getNextInsertLocationNode: (
    node: INode<IPolicyTemplateSlot>,
    direction: any
  ) => INode<IPolicyTemplateSlot> | undefined;
  selectNodes: (nodeIds: string[]) => void;
  clearSelection: () => void;
  addTemplateToPolicy?: (template: IPublishedTemplate) => void;
  deleteSelectedTemplates?: () => void;
  addCriteriaToTemplate?: (templateId: string, criteria: IPolicySlotConstraint) => void;
  removeCriteriaFromTemplate?: (templateId: string, criteriaId: string) => void;
  updateTemplateCriteria?: (templateId: string, criteria: IPolicySlotConstraint[]) => void;
  selectedTemplates: IPolicyTemplateSlot[];
  policyId?: string;
  policyInfo?: IPolicyInfo;
  isDeleted: boolean;
  setIsDeleted: (value: boolean) => void;
  onCriteriaApplied?: () => void;
  setOnCriteriaApplied?: (callback: () => void) => void;
  isAddingTemplate: boolean;
  isDeletingTemplate: boolean;
}

const Context = createContext<IPolicyNodeTreeProvider>({} as IPolicyNodeTreeProvider);

export const usePolicyEditStructureNodeManager = () => {
  const context = useContext(Context);
  
  if (!context) {
    // Return mock implementation for now
    const mockNodeTree: INodeTree<IPolicyTemplateSlot> = {
      nodes: {},
      childNodeMappings: {},
      observable: new Observable(),
    };

    const mockNodeSelection: INodeSelectionTracker = {
      selections: new NodeSelection(),
      observable: new Observable(),
    };

    return {
      nodeTree: mockNodeTree,
      nodeSelection: mockNodeSelection,
      getSelectedNodes: () => [],
      getNextInsertLocationNode: () => undefined,
      getChildNodes: () => [],
      selectNodes: () => {},
      clearSelection: () => {},
      addTemplateToPolicy: () => {},
      deleteSelectedTemplates: () => {},
      addCriteriaToTemplate: () => {},
      removeCriteriaFromTemplate: () => {},
      updateTemplateCriteria: () => {},
      selectedTemplates: [],
      policyId: undefined,
      policyInfo: undefined,
      isDeleted: false,
      setIsDeleted: () => {},
      onCriteriaApplied: () => {},
      setOnCriteriaApplied: () => {},
      isAddingTemplate: false,
      isDeletingTemplate: false,
    };
  }

  return context;
};

// Provider component for policy edit structure node management
export function PolicyEditStructureNodeManagerProvider({
  children,
  policyId,
  policyInfo,
}: {
  children: React.ReactNode;
  policyId?: string;
  policyInfo?: IPolicyInfo;
}) {
  const [updatePolicy] = useUpdatePolicyMutation();
  
  // State to track added templates
  const [templateNodes, setTemplateNodes] = useState<Record<string, INode<IPolicyTemplateSlot>>>({});
  
  // State to track selected nodes
  const [selectedNodeIds, setSelectedNodeIds] = useState<string[]>([]);
  
  // State to track deleted state
  const [isDeleted, setIsDeleted] = useState(false);
  
  // State to track when template is being added
  const [isAddingTemplate, setIsAddingTemplate] = useState(false);
  
  // State to track when template is being deleted
  const [isDeletingTemplate, setIsDeletingTemplate] = useState(false);
  
  // Callback to be called when criteria is applied (used for refetching data)
  const [onCriteriaAppliedCallback, setOnCriteriaAppliedCallback] = useState<(() => void) | undefined>(undefined);
  
  // Setter for the onCriteriaApplied callback
  const setOnCriteriaApplied = useCallback((callback: () => void) => {
    setOnCriteriaAppliedCallback(() => callback);
  }, []);
  
  // Helper function to convert KQL criteria back to UI constraint format
  const convertKQLToConstraints = useCallback((criteria: Array<{ filterType: string; source: string; query: string }>) => {
    if (!criteria || criteria.length === 0) {
      return [];
    }

    return criteria.flatMap((criterion) => {
      // Parse KQL query format:
      // AgreementAudit
      // | where Template.Id == '<templateId>'
      //   and Constraints.Country !in ('FR', 'CN')
      //   and Status == 'Active'
      //   and StartEffectiveDate < datetime('2025-01-11T00:00:00Z')
      // | summarize arg_max(_timestamp, *) by CustomerId
      
      const { query } = criterion;
      const constraints: any[] = [];
      
      // Extract constraint expressions between 'where Template.Id' and 'Status == Active'
      // or between 'where Template.Id' and '| summarize' if no Status filter
      const whereMatch = query.match(/where (?:Template\.Id|TemplateId)[^|]+/);
      if (!whereMatch) {
        return constraints;
      }
      
      const whereClauses = whereMatch[0];
      
      // Split by ' and ' to get individual expressions
      const expressions = whereClauses.split(/\s+and\s+/);
      
      expressions.forEach((expr: string, index: number) => {
        const trimmedExpr = expr.trim();
        
        // Skip system filters (TemplateId, Status, StartEffectiveDate) - only process Constraints.*
        if (!trimmedExpr.startsWith('Constraints.')) {
          return;
        }
        
        // Match pattern: "Constraints.FieldName operator (values)" or "Constraints.FieldName operator 'value'"
        // Handles: "!in", "in" (KQL syntax)
        const arrayMatch = trimmedExpr.match(/Constraints\.(\w+)\s+(!in|in)\s+\(([^)]+)\)/);
        const singleMatch = trimmedExpr.match(/Constraints\.(\w+)\s+(!=|==|=)\s+'([^']+)'/);
        
        if (arrayMatch) {
          const [, fieldName, operator, valuesStr] = arrayMatch;
          const isExclude = operator === '!in';
          const values = valuesStr.split(',').map((v: string) => v.trim().replace(/'/g, ''));
          
          constraints.push({
            id: `${fieldName}-${index}`,
            name: fieldName,
            value: values[0] || "",
            values,
            valueDisplay: values[0] || "",
            valueDisplays: values,
            operator: isExclude ? "Exclude" : "Include",
            key: fieldName,
            keyId: fieldName,
            keyDisplay: fieldName,
          } as IPolicySlotConstraint);
        } else if (singleMatch) {
          const [, fieldName, operator, value] = singleMatch;
          const isExclude = operator === '!=';
          
          constraints.push({
            id: `${fieldName}-${index}`,
            name: fieldName,
            value,
            values: [value],
            valueDisplay: value,
            valueDisplays: [value],
            operator: isExclude ? "Exclude" : "Include",
            key: fieldName,
            keyId: fieldName,
            keyDisplay: fieldName,
          } as IPolicySlotConstraint);
        }
      });
      
      return constraints;
    });
  }, []);
  
  // Initialize template nodes from policyInfo if template exists
  useEffect(() => {
    if (policyInfo?.template && policyInfo?.templateId) {
      const nodeId = `template-${policyInfo.templateId}`;
      const existingNode = templateNodes[nodeId];
      
      // Convert criteria from KQL format to UI constraints
      const constraints = policyInfo.criteria && policyInfo.criteria.length > 0 
        ? convertKQLToConstraints(policyInfo.criteria) 
        : undefined;
      
      // If node doesn't exist, create it
      if (!existingNode) {
        const templateSlot: IPolicyTemplateSlot = {
          id: nodeId,
          name: policyInfo.template,
          description: undefined,
          templateId: policyInfo.templateId,
          templateName: policyInfo.template,
          position: 0,
          isRequired: false,
          constraints,
        };
        
        const templateNode: INode<IPolicyTemplateSlot> = {
          id: nodeId,
          parentId: undefined,
          isOpen: false,
          height: 0,
          content: templateSlot,
          globalPosition: 0,
          depth: 0,
        };

        setTemplateNodes({
          [nodeId]: templateNode,
        });
      } else if (constraints !== undefined && existingNode.content) {
        // If node exists but criteria arrived later, update it
        const hasConstraints = existingNode.content.constraints && existingNode.content.constraints.length > 0;
        const needsUpdate = !hasConstraints && constraints && constraints.length > 0;
        
        if (needsUpdate) {
          const updatedTemplateSlot: IPolicyTemplateSlot = {
            ...existingNode.content,
            constraints,
          };
          
          const updatedNode: INode<IPolicyTemplateSlot> = {
            ...existingNode,
            content: updatedTemplateSlot,
          };

          setTemplateNodes(prev => ({
            ...prev,
            [nodeId]: updatedNode,
          }));
        }
      }
    }
  }, [policyInfo?.template, policyInfo?.templateId, policyInfo?.criteria, convertKQLToConstraints, templateNodes]);

  // Mock implementation - will be replaced with actual policy slot management
  const mockNodeTree: INodeTree<IPolicyTemplateSlot> = useMemo(() => ({
    nodes: templateNodes,
    childNodeMappings: {},
    observable: new Observable(),
  }), [templateNodes]);

  const mockNodeSelection: INodeSelectionTracker = useMemo(() => ({
    selections: new NodeSelection(),
    observable: new Observable(),
  }), []);

  const getSelectedNodes = useCallback(() => 
    selectedNodeIds.map(id => templateNodes[id]).filter(Boolean), 
    [selectedNodeIds, templateNodes]);
  
  const getNextInsertLocationNode = useCallback(() => undefined, []);
  const getChildNodes = useCallback(() => [], []);
  
  const selectNodes = useCallback((nodeIds: string[]) => {
    setSelectedNodeIds(nodeIds);
    // Update the mock selection tracker as well
    mockNodeSelection.selections.clear();
    nodeIds.forEach(id => mockNodeSelection.selections.addSelection(id, new Set()));
  }, [mockNodeSelection]);
  
  const clearSelection = useCallback(() => {
    setSelectedNodeIds([]);
    mockNodeSelection.selections.clear();
  }, [mockNodeSelection]);

  // Add template to policy function
  const addTemplateToPolicy = useCallback(async (template: IPublishedTemplate) => {
    const existingNodes = Object.keys(templateNodes);
    
    // Set loading state
    setIsAddingTemplate(true);
    
    // Reset deleted state when adding template
    setIsDeleted(false);
    
    // If there's exactly one template, update it instead of adding a new one
    if (existingNodes.length === 1) {
      const existingNodeId = existingNodes[0];
      
      // Create fresh template slot WITHOUT old constraints
      const updatedTemplateSlot: IPolicyTemplateSlot = {
        id: existingNodeId,
        templateId: template.key,
        templateName: template.name,
        name: template.name,
        description: undefined,
        position: 0,
        isRequired: false,
        // Do NOT include constraints - they will be loaded fresh from API
      };
      
      const updatedNode: INode<IPolicyTemplateSlot> = {
        id: existingNodeId,
        parentId: undefined,
        isOpen: false,
        height: 0,
        content: updatedTemplateSlot,
        globalPosition: 0,
        depth: 0,
      };
      
      setTemplateNodes({
        [existingNodeId]: updatedNode,
      });
      
      // Call the API to update the policy with the selected template
      if (policyId && policyInfo) {
        try {
          await updatePolicy({
            id: policyId,
            templateId: template.key,
            templateName: template.name,
            cloud: environmentConfig.cloud,
            environment: environmentConfig.environment,
            policyType: policyInfo.type || "Implicit Acceptance",
          }).unwrap();
          
          // Trigger refetch to get latest data from API
          if (onCriteriaAppliedCallback) {
            onCriteriaAppliedCallback();
          }
        } catch (error) {
          // Failed to update policy with template
        } finally {
          setIsAddingTemplate(false);
        }
      }
      
      return;
    }
    
    // Otherwise, add a new template as usual
    const nodeId = `template-${template.key}-${Date.now()}`;
    const templateSlot: IPolicyTemplateSlot = {
      id: nodeId,
      name: template.name,
      description: undefined, // IPublishedTemplate doesn't have description
      templateId: template.key,
      templateName: template.name,
      position: Object.keys(templateNodes).length,
      isRequired: false,
    };
    
    const templateNode: INode<IPolicyTemplateSlot> = {
      id: nodeId,
      parentId: undefined,
      isOpen: false,
      height: 0,
      content: templateSlot,
      globalPosition: Object.keys(templateNodes).length,
      depth: 0,
    };

    setTemplateNodes(prev => ({
      ...prev,
      [nodeId]: templateNode,
    }));
    
    // Call the API to update the policy with the selected template
    if (policyId && policyInfo) {
      try {
        await updatePolicy({
          id: policyId,
          templateId: template.key,
          templateName: template.name,
          cloud: environmentConfig.cloud,
          environment: environmentConfig.environment,
          policyType: policyInfo.type || "Implicit Acceptance",
        }).unwrap();
        
        // Trigger refetch to get latest data from API
        if (onCriteriaAppliedCallback) {
          onCriteriaAppliedCallback();
        }
      } catch (error) {
        // Failed to update policy with template
      } finally {
        setIsAddingTemplate(false);
      }
    }
  }, [templateNodes, policyId, policyInfo, updatePolicy, onCriteriaAppliedCallback]);

  // Delete selected templates function
  const deleteSelectedTemplates = useCallback(async () => {
    if (selectedNodeIds.length === 0) return;
    
    // Show loading spinner first
    setIsDeletingTemplate(true);
    
    // Clear selection after deletion
    clearSelection();
    
    // Call the API to clear the template from the policy
    if (policyId && policyInfo) {
      try {
        await updatePolicy({
          id: policyId,
          templateId: null,
          templateName: null,
          cloud: environmentConfig.cloud,
          environment: environmentConfig.environment,
          policyType: policyInfo.type || "Implicit Acceptance",
        }).unwrap();
        
        // Trigger refetch to get latest data from API
        if (onCriteriaAppliedCallback) {
          onCriteriaAppliedCallback();
        }
      } catch (error) {
        // Failed to clear policy template
      } finally {
        // Clear nodes and set deleted state after API call completes
        setTemplateNodes({});
        setIsDeleted(true);
        setIsDeletingTemplate(false);
      }
    }
  }, [selectedNodeIds, clearSelection, policyId, policyInfo, updatePolicy, onCriteriaAppliedCallback]);

  // Add criteria to template function
  const addCriteriaToTemplate = useCallback((templateId: string, criteria: IPolicySlotConstraint) => {
    setTemplateNodes(prev => {
      const templateNode = prev[templateId];
      if (!templateNode || !templateNode.content) return prev;

      const updatedTemplate: IPolicyTemplateSlot = {
        ...templateNode.content,
        constraints: [
          ...(templateNode.content.constraints || []),
          criteria,
        ],
      };

      const updatedNode: INode<IPolicyTemplateSlot> = {
        ...templateNode,
        content: updatedTemplate,
      };

      return {
        ...prev,
        [templateId]: updatedNode,
      };
    });
  }, []);

  // Remove criteria from template function
  const removeCriteriaFromTemplate = useCallback((templateId: string, criteriaId: string) => {
    setTemplateNodes(prev => {
      const templateNode = prev[templateId];
      if (!templateNode || !templateNode.content) return prev;

      const updatedTemplate: IPolicyTemplateSlot = {
        ...templateNode.content,
        constraints: (templateNode.content.constraints || []).filter(c => c.id !== criteriaId),
      };

      const updatedNode: INode<IPolicyTemplateSlot> = {
        ...templateNode,
        content: updatedTemplate,
      };

      return {
        ...prev,
        [templateId]: updatedNode,
      };
    });
  }, []);

  // Update template criteria function - replace all criteria
  const updateTemplateCriteria = useCallback((templateId: string, criteria: IPolicySlotConstraint[]) => {
    setTemplateNodes(prev => {
      const templateNode = prev[templateId];
      if (!templateNode || !templateNode.content) return prev;

      const updatedTemplate: IPolicyTemplateSlot = {
        ...templateNode.content,
        constraints: criteria,
      };

      const updatedNode: INode<IPolicyTemplateSlot> = {
        ...templateNode,
        content: updatedTemplate,
      };

      return {
        ...prev,
        [templateId]: updatedNode,
      };
    });
  }, []);

  // Get selected templates for criteria editing
  const selectedTemplates = useMemo(() => 
    selectedNodeIds.map(id => templateNodes[id]?.content).filter((content): content is IPolicyTemplateSlot => !!content), 
    [selectedNodeIds, templateNodes]);

  const contextValue: IPolicyNodeTreeProvider = useMemo(() => ({
    nodeTree: mockNodeTree,
    nodeSelection: mockNodeSelection,
    getSelectedNodes,
    getChildNodes,
    getNextInsertLocationNode,
    selectNodes,
    clearSelection,
    addTemplateToPolicy,
    deleteSelectedTemplates,
    addCriteriaToTemplate,
    removeCriteriaFromTemplate,
    updateTemplateCriteria,
    selectedTemplates,
    policyId,
    policyInfo,
    isDeleted,
    setIsDeleted,
    onCriteriaApplied: onCriteriaAppliedCallback,
    setOnCriteriaApplied,
    isAddingTemplate,
    isDeletingTemplate,
  }), [mockNodeTree, mockNodeSelection, getSelectedNodes, getChildNodes, getNextInsertLocationNode, selectNodes, clearSelection, addTemplateToPolicy, deleteSelectedTemplates, addCriteriaToTemplate, removeCriteriaFromTemplate, updateTemplateCriteria, selectedTemplates, policyId, policyInfo, isDeleted, onCriteriaAppliedCallback, setOnCriteriaApplied, isAddingTemplate, isDeletingTemplate]);

  return (
    <Context.Provider value={contextValue}>
      {children}
    </Context.Provider>
  );
}